import React, { useMemo, useRef, useEffect, useState, FocusEvent } from "react";
import { ReactDatePickerProps } from "react-datepicker";
import { TimeField, ITimeFieldProps } from "./TimeField";
import moment from "moment-timezone";
import { isIsoDate, isEpochDate } from "src/utils/index";
import { Button, Grid, Tooltip, InputLabel, FormControl, FormLabel, alpha, Box } from "@mui/material";

import { OCFormControl } from "./FormControl";
// Formik imports
import { FieldMetaProps } from "formik";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FieldError } from "./FieldError";
import { useCallback } from "react";
import { yearInMilliseconds } from "@vmosolutions/oc-interfaces";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIconField } from "./CalendarIconField";

export interface IDateTimeFieldProps {
  name: string;
  value?: string | number;
  label?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string | number) => any;
  className?: string;
  showAirportTime?: string;
  hideTimeControl?: boolean;
  hideLocalizeBtn?: boolean;
  localizeDisplay?: boolean;
  displayOnlyTime?: boolean;
  isEpoch?: boolean;
  disabled?: boolean;
  DateProps?: Partial<ReactDatePickerProps>;
  TimeProps?: ITimeFieldProps;
  meta?: Partial<FieldMetaProps<any>>;
  minDate?: string | number;
  maxDate?: string | number;
  showCalendarIcon?: boolean;
  labelSX?: any;
}

interface IValidateDateTimeResponse {
  value: string | number;
  valid: boolean;
  errorString: string;
  date?: { value: string; valid: boolean };
  time?: { value: string; valid: boolean };
  remainder?: { value: string; valid: boolean };
}

export const validateDateTimeFormat = (value: string | number): IValidateDateTimeResponse => {
  if (!value) {
    return { value: "", valid: true, errorString: "" };
  }

  if (isEpochDate(value)) {
    return { value: value, valid: true, errorString: "" };
  }

  const checkDate = isIsoDate(value as string);

  let errorStr = "";
  // ISO Date value is incorrect formatted (unknown issue)
  if (!checkDate.valid && checkDate.type === "unknown") {
    errorStr = "Date/time format incorrect.";
  } else if (!checkDate.valid && checkDate.type === "isoString") {
    // Format has been detected as ressembling an ISO string but a component is incorrect
    const dateStr = checkDate.value.slice(0, 10);
    const timeStr = checkDate.value.slice(11, 16);
    const remainderStr = checkDate.value.slice(17, 24);
    // Check Date
    const invalidDate = !/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(dateStr);
    const invalidTime = !/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]$/.test(timeStr);
    const invalidRemainder = !/^([0-5][0-9])(.[0-9]+)?(Z)?$/.test(remainderStr);

    switch (true) {
      case invalidDate:
        errorStr = "Date format incorrect.";
        break;
      case invalidTime:
        errorStr = "Time format incorrect.";
        break;
      case invalidRemainder:
        errorStr = "Timezone incorrect.";
        break;
      default:
      // do nothing
    }
    return {
      value: value,
      valid: errorStr.length === 0,
      errorString: errorStr,
      date: { value: dateStr, valid: !invalidDate },
      time: { value: timeStr, valid: !invalidTime },
      remainder: { value: remainderStr, valid: !invalidRemainder },
    };
  }
  return { value: value, valid: errorStr.length === 0, errorString: errorStr };
};

// Adds timezone offset when supplied with UTC Date.
const returnAirportDateTime = (utcDateTime: string, timezone: string): string => {
  if (!timezone || !isIsoDate(utcDateTime)) {
    return utcDateTime;
  }
  const UTCTimezoneOffset = moment(utcDateTime).tz(timezone).utcOffset(); // Returns the utc offset of the supplied timezone.
  const momentDate = moment(utcDateTime);
  momentDate.add(UTCTimezoneOffset, "minutes");
  return momentDate.toISOString();
};

// Removes offset when supplied with the airport Date string.
const returnUTCDateTime = (airportDateTime: string, timezone: string): string => {
  if (!timezone || !isIsoDate(airportDateTime)) {
    return airportDateTime;
  }
  const UTCTimezoneOffset = moment(airportDateTime).tz(timezone).utcOffset(); // Returns the utc offset of the supplied timezone.
  const momentDate = moment(airportDateTime);
  momentDate.subtract(UTCTimezoneOffset, "minutes");
  return momentDate.toISOString();
};

// Extract the time from the supplied ISO string (gets passed to the time input component.)
const extractTime = (IsoDate: string): string => IsoDate.substr(11, 5);

/**
 * DATE TIME INPUT component
 */
export const DateTimeField = (props: IDateTimeFieldProps): JSX.Element => {
  const {
    name,
    label,
    onFocus,
    onBlur,
    value,
    onChange,
    className,
    hideTimeControl,
    showAirportTime,
    displayOnlyTime,
    hideLocalizeBtn,
    localizeDisplay,
    isEpoch,
    disabled,
    DateProps,
    TimeProps,
    meta,
    minDate = new Date()?.valueOf() - 100 * yearInMilliseconds,
    maxDate = new Date()?.valueOf() + 100 * yearInMilliseconds,
    showCalendarIcon,
    labelSX,
  } = props;

  const labelRef = useRef<HTMLDivElement>(null);
  const DatePickerRef = useRef(null);
  const [displayAsUTC, setdisplayAsUTC] = useState<boolean>(true);
  const [airportIdent, setAirportIdent] = useState<string>(null);
  const [timefieldValue, setTimeFieldValue] = useState<string>(null);
  const [timefieldError, setTimeFieldError] = useState<string>(null);
  const [timezone, setTimezone] = useState<string>(null);
  const [utcDateValue, setUtcDateValue] = useState<string>(null);
  const [airportDateValue, setAirportDateValue] = useState<string>(null);
  const [valueIsEpoch, setValueIsEpoch] = useState<boolean>(isEpoch);
  const [displayDate, setDisplayDate] = useState(displayAsUTC ? utcDateValue : airportDateValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  // Set displayed Date to UTC or Airport Date depending on which one the user has selected.

  // If localizeDisplay props changes, modify internal display value.
  useEffect(() => {
    const showLocal = localizeDisplay && !!showAirportTime;
    setdisplayAsUTC(!showLocal);
  }, [localizeDisplay, showAirportTime]);

  useEffect(() => {
    setDisplayDate(displayAsUTC ? utcDateValue : airportDateValue);
  }, [displayAsUTC, utcDateValue, airportDateValue]);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [value, meta]);

  const isValidDate = useMemo(() => displayOnlyTime || !value || moment(value).isValid(), [displayOnlyTime, value]);

  useEffect(() => {
    const isE =
      isEpoch || (value !== null && value !== undefined && typeof value === "number" && !!new Date(value))
        ? true
        : false;
    // Check to make certain the value is a valid date type.
    const isValid = displayOnlyTime || moment(value).isValid();
    if (isValid) {
      const dateValue =
        isE && value !== null && value !== undefined ? new Date(value).toISOString() : isE && !value ? null : value;
      setValueIsEpoch(isE);
      setUtcDateValue(dateValue as string);
    }
  }, [name, value, isEpoch, displayOnlyTime]);

  // On update airportIdent, retrieve retrieve and set timezone for associated airport.
  useEffect(() => {
    if (!displayOnlyTime && !!showAirportTime && airportIdent !== showAirportTime) {
      const newTimezone = airportOptions[showAirportTime]?.timezoneId;
      if (newTimezone) {
        setTimezone(newTimezone);
        setAirportIdent(showAirportTime);
      }
    }
  }, [airportIdent, airportOptions, showAirportTime, displayOnlyTime]);

  // On update of timezone or utcdate, fetch airportDateTime.
  useEffect(() => {
    if (!!timezone && !!utcDateValue) {
      const airportDateTime = returnAirportDateTime(utcDateValue, timezone);
      setAirportDateValue(airportDateTime);
    } else {
      setAirportDateValue(null);
    }
  }, [timezone, utcDateValue]);

  // Since React DatePicker only works with local time internally, shift the date being passed in so the timezone date selected will always appear correct.
  const displayAsLocal = (isoString: string): Date => {
    if (!isoString) {
      return null;
    }
    const tempDate = new Date(isoString);
    // Add a timezone offset to the UTC time (as the datepicker displays in local mode only) (IN)
    const localDate = new Date(tempDate.getTime() + tempDate.getTimezoneOffset() * 60000);
    return localDate;
  };

  // Convert incoming min/max epochs or isotrings into Date objects compatible with the DatePicker.
  const adjustedDateProps = useMemo(() => {
    if (!DateProps && !maxDate && !minDate) {
      return null;
    }
    const applyLocalShift = timezone && !displayAsUTC;
    const isoMin =
      minDate && typeof minDate === "string" ? minDate : minDate ? new Date(minDate).toISOString() : undefined;
    const isoMax =
      maxDate && typeof maxDate === "string" ? maxDate : maxDate ? new Date(maxDate).toISOString() : undefined;

    //!! Made certain any timezone offsets applied to the value, are also applied to the min and max values.
    const adjustedMinDate =
      isoMin && applyLocalShift
        ? displayAsLocal(returnAirportDateTime(isoMin, timezone))
        : isoMin && !applyLocalShift
          ? displayAsLocal(isoMin)
          : undefined;
    const adjustedMaxDate =
      isoMax && applyLocalShift
        ? displayAsLocal(returnAirportDateTime(isoMax, timezone))
        : isoMax && !applyLocalShift
          ? displayAsLocal(isoMax)
          : undefined;
    return { ...DateProps, minDate: adjustedMinDate, maxDate: adjustedMaxDate };
  }, [DateProps, maxDate, minDate, timezone, displayAsUTC]);

  // Checks the value that was input into the component and returns the same type.
  const outputSameAsInputType = useCallback(
    (date: string): number | string => {
      return valueIsEpoch ? new Date(date)?.valueOf() : date;
    },
    [valueIsEpoch]
  );

  // HANDLE DATE CHANGE
  const handleDateChange = useCallback(
    (event: any, context: any): void => {
      const newDate = event ? new Date(event) : null;
      if (!!newDate && typeof newDate.toISOString === "function") {
        if (isNaN(newDate.getTime())) {
          setDisplayDate(event.toString());
          return;
        }
        // Remove the timezone offset applied to the DatePicker local time. (OUT)
        const eventDate = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000).toISOString();
        // If UTC is on, apply offset to airport time. If off, remove it to get UTC Date.
        const newUTCtime = displayAsUTC ? eventDate : returnUTCDateTime(eventDate, timezone);
        const newAirportTime = displayAsUTC ? returnAirportDateTime(eventDate, timezone) : eventDate;
        setUtcDateValue(newUTCtime);
        setAirportDateValue(newAirportTime);
        if (onChange) onChange(outputSameAsInputType(newUTCtime));
      } else {
        setUtcDateValue(null);
        setAirportDateValue(null);
        if (onChange) onChange(null);
      }
    },
    [displayAsUTC, onChange, outputSameAsInputType, timezone]
  );

  // HANDLE TIME CHANGE
  const handleTimeChange = useCallback(
    (time: string, error?: string): void => {
      // If there's a time field error, return without modifying date.
      if (error) {
        setTimeFieldValue(time);
        setTimeFieldError(error);
        return;
      }
      const value = time ?? "00:00";
      let newDisplayedValue: string;
      if (displayOnlyTime !== true) {
        const previousDate = displayAsUTC ? utcDateValue : airportDateValue;
        const oldMinutes = parseInt(previousDate.slice(11, 13)) * 60 + parseInt(previousDate.slice(14, 16));
        const newMinutes = parseInt(value.slice(0, 2)) * 60 + parseInt(value.slice(3, 5));
        // Debug.log(newMinutes);
        const minuteShift = newMinutes - oldMinutes;
        // Apply the time shift and update values.
        const newUTCtime = moment(utcDateValue).add(minuteShift, "minutes").toISOString();
        const newAirportTime = moment(airportDateValue).add(minuteShift, "minutes").toISOString();
        setUtcDateValue(newUTCtime);
        setAirportDateValue(newAirportTime);
        newDisplayedValue = newUTCtime;
      } else {
        newDisplayedValue = value;
      }
      setTimeFieldValue(null);
      setTimeFieldError(null);
      if (onChange) onChange(outputSameAsInputType(newDisplayedValue));
    },
    [airportDateValue, displayAsUTC, displayOnlyTime, onChange, outputSameAsInputType, utcDateValue]
  );

  const handleFocusDate = useCallback(
    (e: FocusEvent<HTMLInputElement>): void => {
      setIsFocused(true);
      DatePickerRef?.current?.setOpen(true);
      if (onFocus && typeof onFocus === "function") {
        onFocus(e);
      }
    },
    [DatePickerRef, onFocus]
  );

  const handleBlurDate = useCallback(
    (e: FocusEvent<HTMLInputElement>): void => {
      setIsFocused(false);
      if (onBlur && typeof onBlur === "function") {
        onBlur(e);
      }
    },
    [onBlur]
  );

  const handleFocusTime = useCallback(
    (e: FocusEvent<HTMLInputElement>): any => {
      if (!showCalendarIcon) {
        DatePickerRef?.current?.setOpen(true);
      }
      setIsFocused(true);
      if (onFocus && typeof onFocus === "function") {
        onFocus(e);
      }
    },
    [DatePickerRef, onFocus, showCalendarIcon]
  );

  /** Blurring calendar has to be delayed for time picker so that date selections can be made before it closes. - CC */
  const handleBlurTime = useCallback(
    (e: FocusEvent<HTMLInputElement>): any => {
      e.persist();
      setTimeout(() => {
        if (onBlur && typeof onBlur === "function") {
          onBlur(e);
        }
      }, 0); // Setting this from 500 to 0 to resolve a tabbing sort order bug. Not certain why I had originally set it to 500 but it seems to work fine with 0 - CC
    },
    [onBlur]
  );

  const labelWidth: number = !!labelRef && !!labelRef.current ? labelRef.current.clientWidth : 0;
  const hasError = meta?.error || timefieldError || !isValidDate ? true : false;

  const DateFieldStyles = (theme: any): any => ({
    dateTime: {
      "& .error": {
        boxShadow: `${alpha(theme.palette.error.dark, 0.25)} 0 0 0 0.2rem !important`,
        transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        borderColor: `${theme.palette.error.dark} !important`,
        backgroundColor: `${alpha(theme.palette.error.dark, 0.25)} !important`,
      },
      "& .MuiInputBase-input": {
        borderRadius: "4px",
        position: "relative",
        backgroundColor: theme.custom.forms.inputBack,
        border: "1px solid",
        borderColor: theme.custom.forms.inputBorder,
        fontSize: 11,
        padding: "4px 6px",
        color: theme.palette.common.white,
        transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
        "&:focus, &:hover": {
          boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
          borderColor: theme.palette.primary.main,
        },
        "&::placeholder, &::-webkit-input-placeholder": {
          color: theme.custom.forms.labels,
          opacity: 1,
        },
      },
    },
    timefield: {
      display: "inline",
      position: "relative",
      left: "-16px",
      "& input": { textAlign: "center" },
    },
  });

  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: hasError ? "space-between" : "flex-end",
            textAlign: "right",
          }}
          xs
        >
          <FieldError meta={meta} icon={true} />
          {label && (
            <FormLabel sx={{ ...labelSX }} htmlFor={"id-" + name + "-date"}>
              {label}
            </FormLabel>
          )}
        </Grid>
        <Grid item xs={7}>
          <Tooltip
            enterDelay={500}
            placement="right"
            title={displayOnlyTime !== true ? (timezone && !displayAsUTC ? timezone : "UTC") : ""}
            disableHoverListener={(!timezone && !displayAsUTC) || !hideLocalizeBtn || disabled}
            disableFocusListener={(!timezone && !displayAsUTC) || !hideLocalizeBtn || disabled}
            disableTouchListener={(!timezone && !displayAsUTC) || !hideLocalizeBtn || disabled}
          >
            <div className="timezone_tooltip">
              {displayOnlyTime !== true && (
                <>
                  {!showCalendarIcon && (
                    <DatePicker
                      disabled={disabled}
                      value={displayDate ? moment(displayAsLocal(displayDate.toString())) : null}
                      onChange={(value, context) => handleDateChange(value, context)}
                      format="MMM DD, YYYY"
                      minDate={moment(adjustedDateProps.minDate)}
                      maxDate={moment(adjustedDateProps.maxDate)}
                      slotProps={{
                        openPickerButton: {
                          tabIndex: -1,
                          sx: {
                            padding: "2px !important",
                          },
                        },
                        textField: {
                          id: `${name}_d_input`,
                          classes: { root: "dateTime" },
                          className: "input-base",
                        },
                        day: {
                          classes: { root: "selectedDay" },
                        },
                      }}
                      sx={(theme) => DateFieldStyles(theme).dateTime}
                    />
                  )}
                  {showCalendarIcon && (
                    <CalendarIconField
                      value={moment(displayAsLocal(displayDate?.toString()))}
                      disabled={disabled}
                      offset={{ x: 0, y: -400 }}
                      onChange={(value, context) => handleDateChange(value.toDate(), context)}
                      minDate={moment(adjustedDateProps.minDate)}
                      maxDate={moment(adjustedDateProps.maxDate)}
                    />
                  )}
                </>
              )}

              {(hideTimeControl !== true || !!showAirportTime) && (
                <Box sx={(theme) => DateFieldStyles(theme).timefield}>
                  <TimeField
                    id={name + "_t_input"}
                    disabled={(!displayDate && displayOnlyTime !== true) || disabled}
                    placeholder="HH:mm"
                    value={
                      !!displayDate && displayOnlyTime !== true
                        ? timefieldValue || extractTime(displayDate)
                        : timefieldValue || displayDate
                    }
                    onFocus={handleFocusTime}
                    onBlur={handleBlurTime}
                    onChange={handleTimeChange}
                    {...TimeProps}
                  />
                </Box>
              )}
            </div>
          </Tooltip>

          {!!showAirportTime && displayOnlyTime !== true && hideLocalizeBtn !== true && (
            <div className="timeformat-buttons">
              <Tooltip title="Coordinated Universal Time" enterDelay={1000} placement="top">
                <span>
                  <Button
                    id="time_utc_btn"
                    variant={displayAsUTC ? "contained" : "outlined"}
                    className="timeUTCButton"
                    color="primary"
                    classes={{ disabled: "disabled" }}
                    disabled={displayAsUTC}
                    onClick={() => setdisplayAsUTC(true)}
                    size="small"
                  >
                    UTC
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={timezone ? timezone : ""} enterDelay={1000} placement="top">
                <span>
                  <Button
                    id="time_air_btn"
                    variant={displayAsUTC ? "outlined" : "contained"}
                    className="timeAirportButton"
                    color="primary"
                    classes={{ disabled: "disabled" }}
                    disabled={!displayAsUTC}
                    onClick={() => setdisplayAsUTC(false)}
                    size="small"
                  >
                    {showAirportTime}
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
